import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Box,
  Bullet,
  Card,
  Heading,
  Inline,
  Stack,
  Text,
  VisuallyHidden,
} from "@generalapplications/ui";

import { RevealItem } from "@/app/common/components/composite/motion/RevealGroup";
import type { DeliveryPhase } from "@/content/delivery";
import type { ServicePhaseDetail } from "@/content/services";

export type AccordionItemType = "produces" | "what-else";

export type PhaseCardProps = {
  phase: DeliveryPhase;
  detail?: ServicePhaseDetail;
  defaultOpenItem?: AccordionItemType | null;
};

/** One delivery phase: what every service does in it, then this one's path. */
export function PhaseCard({
  phase,
  detail,
  defaultOpenItem = null,
}: PhaseCardProps) {
  const hasProduces = Boolean(phase.produces.length);
  const hasWhatElse = Boolean(detail?.produces?.length);

  return (
    <RevealItem as="li">
      <Card radius="xxl" padding="none" height="full">
        {/* Band 1 — shared across every service. */}
        <Box padding="lg" grow>
          <Stack gap="sm">
            <Inline gap="sm">
              <Badge variant="outline" aria-hidden="true">
                {phase.step}
              </Badge>
              <Heading level={3} size="sm">
                <VisuallyHidden>{`Phase ${phase.step}: `}</VisuallyHidden>
                {phase.name}
              </Heading>
            </Inline>
            <Text>{phase.summary}</Text>
          </Stack>
        </Box>

        {/* Band 2 — this service's path through that phase. */}
        {detail && (
          <Box padding="lg" border="top" background="muted">
            <Stack gap="md">
              <Text variant="label" weight="semibold">
                {detail.focus}
              </Text>
              <Card variant="soft" padding="none">
                <Accordion
                  defaultValue={defaultOpenItem ? [defaultOpenItem] : []}
                >
                  {hasProduces && (
                    <PhaseList
                      value="produces"
                      label="Produces"
                      items={phase.produces}
                    />
                  )}
                  {hasWhatElse && (
                    <PhaseList
                      value="what-else"
                      label="What else"
                      items={detail.produces!}
                    />
                  )}
                </Accordion>
              </Card>
            </Stack>
          </Box>
        )}
      </Card>
    </RevealItem>
  );
}

type PhaseListProps = {
  value: AccordionItemType;
  label: string;
  items: string[];
};

function PhaseList({ value, label, items }: PhaseListProps) {
  return (
    <AccordionItem value={value}>
      <AccordionTrigger paddingX="sm">
        <Inline gap="sm" align="center">
          {label}
          <Badge size="sm" tone="muted">
            {items.length}
          </Badge>
        </Inline>
      </AccordionTrigger>
      <AccordionContent flush>
        <Box paddingX="md" paddingY="sm">
          <Stack as="ul" gap="xs">
            {items.map((item) => (
              <Inline as="li" key={item} align="start" gap="sm">
                <Bullet />
                <Text variant="label">{item}</Text>
              </Inline>
            ))}
          </Stack>
        </Box>
      </AccordionContent>
    </AccordionItem>
  );
}
